import { Button as DxButton } from 'devextreme-react/button';
import { Column, Summary, ToolbarItem } from 'devextreme-react/data-grid';
import moment from 'moment';
import React, { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useAuth } from '../../../auth';
import Breadcrumb from '../../../components/common/breadcrumb';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import api from '../../../services/api-configuration';
import { getAllWarehouses } from '../../../services/apis';
import { ButtonOnExporting } from '../../../utils/DataGridExporter';
import useFormatMessage from '../../../utils/useFormatMessage';
import DepoIslemleri from './DepoIslemleri';

const INITIAL_WAREHOUSE = {
    address_id: null,
    address_description: '',
    capacity: null,
    create_date: null,
    id: null,
    is_active: true,
    tax_number: '',
    tax_office: '',
    warehouse_code: '',
    warehouse_name: '',
    warehouse_short_name: ''
};

// Memoize edilmiş alt bileşenler
const MemoizedBreadcrumb = React.memo(Breadcrumb);
const MemoizedDepoIslemleri = React.memo(DepoIslemleri);

const StatusIcon = React.memo(({ isActive }) => (
    <i
        className={`fa fa-${isActive ? 'check' : 'times'}`}
        style={{ color: isActive ? 'green' : 'red' }}
    />
));

const ActionButton = React.memo(({ data, onEdit }) => {
    const { hasRole } = useAuth(2);

    if (!hasRole('depo_role_update')) return null;

    return (
        <i
            className="fa fa-edit ml-2"
            onClick={() => onEdit(data)}
            style={{
                cursor: 'pointer',
                color: 'blue',
                fontSize: '18px'
            }}
        />
    );
});

const DepoTanim = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(2);
    const isMounted = useRef(true);
    const gridRef = useRef(null);

    const [isOpen, setIsOpen] = React.useState(false);
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [dataSource, setDataSource] = React.useState([]);
    const [trigger, setTrigger] = React.useState(false);
    const [selected, setSelected] = React.useState(INITIAL_WAREHOUSE);
    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchWarehouses = useCallback(async () => {
        if (!isMounted.current || isLoading) return;

        setIsLoading(true);
        if (gridRef.current) {
            gridRef.current.instance.beginCustomLoading(t('Yükleniyor'));
        }

        try {
            const res = await getAllWarehouses();
            if (isMounted.current) {
                setDataSource(res || []);
            }
        } catch (err) {
            console.error('Warehouse fetch error:', err);
            if (isMounted.current) {
                toast.error(t('Depo Listesi Alınırken hata oluştu'));
                setDataSource([]);
            }
        } finally {
            if (isMounted.current) {
                setIsLoading(false);
                if (gridRef.current) {
                    gridRef.current.instance.endCustomLoading();
                }
            }
        }
    }, []); // t'yi dependency'den kaldırdık

    React.useEffect(() => {
        fetchWarehouses();
    }, [trigger, fetchWarehouses]);

    const handleDelete = useCallback(async () => {
        try {
            const employeeId = parseInt(Buffer.from(localStorage.getItem('employee_id'), 'base64').toString('ascii'));
            await api.post('/warehouse/delete', {
                id: selected.id,
                update_user_id: employeeId
            });

            if (!isMounted.current) return;

            toast.success(t('Depo Silme Başarılı'));
            setDeleteModal(false);
            setTrigger(prev => !prev);
        } catch (err) {
            if (!isMounted.current) return;

            const errorMessage = err.response?.data?.error?.split(':');
            if (errorMessage) {
                const message = t('Depo Silme Başarısız') + ': ' + t(errorMessage[0]);
                toast.error(errorMessage[1] ? `${message} ID: ${t(errorMessage[1])}` : message);
                return;
            }
            toast.error(t('Depo Silme Başarısız'));
        }
    }, [selected.id]);

    const createWarehouseCode = useCallback((num) => {
        return `D${String(num).padStart(8, '0')}`;
    }, []);

    const handleAddNew = useCallback(() => {
        const nextCode = dataSource?.length === 0
            ? 1
            : parseInt((dataSource[0]?.warehouse_code)?.replace('D', '')) + 1;

        setSelected({
            ...INITIAL_WAREHOUSE,
            warehouse_code: createWarehouseCode(nextCode)
        });
        setIsOpen(true);
    }, [dataSource, createWarehouseCode]);

    const handleRefresh = useCallback(() => {
        if (!isLoading) {
            setTrigger(prev => !prev);
        }
    }, [isLoading]);

    const handleExport = useCallback((e) => {
        ButtonOnExporting(
            e,
            gridRef,
            `Depo Listesi - ${moment(new Date()).format('DD/MM/YYYY')}`,
            'Sayafa-1'
        );
    }, []);

    const handleEdit = useCallback((data) => {
        setSelected(data);
        setIsOpen(true);
    }, []);

    const toggleDeleteModal = useCallback(() => setDeleteModal(prev => !prev), []);

    const ActionButtons = () => {
        return (
            <div className="card-toolbar d-flex justify-content-end align-items-center mb-0"
                style={{ gap: '5px' }}
            >
                <DxButton
                    stylingMode="outlined"
                    type="default"
                    onClick={handleRefresh}
                    disabled={isLoading}
                    text=""
                    width={38}
                    height={38}
                >
                    <i className="fa fa-refresh" />
                </DxButton>

                {hasRole('depo_role_write') && (
                    <DxButton
                        type="default"
                        stylingMode="contained"
                        onClick={handleAddNew}
                        disabled={isLoading}
                        width={90}
                        height={38}
                    >
                        <div className="d-flex align-items-center justify-content-center">
                            <i className="fa fa-plus" />&nbsp;
                            <span className="ms-2">{t('Ekle')}</span>
                        </div>
                    </DxButton>
                )}

                {hasRole('depo_role_export') && (
                    <DxButton
                        stylingMode="contained"
                        type="success"
                        onClick={handleExport}
                        disabled={isLoading}
                        width={38}
                        height={38}
                        title={t('Excele Aktar')}
                    >
                        <i className="fa fa-file-excel-o" />
                    </DxButton>
                )}
            </div>
        );
    };

    return (
        <>
            <MemoizedBreadcrumb current={t('Depo Tanım')} />
            <Card className="p-4">
                <ActionButtons />
                <Row>
                    <DatagridCustom
                        dataSource={dataSource}
                        reff={gridRef}
                        allowColumnReordering={false}
                    >
                        <Column
                            caption={t('Depo Kodu')}
                            dataField="warehouse_code"
                            alignment="center"
                            selectedFilterOperation="contains"

                            allowResizing={false}
                        />
                        <Column
                            caption={t('Adı')}
                            dataField="warehouse_name"
                            alignment="center"
                            selectedFilterOperation="contains"


                            allowResizing={false}
                        />
                        <Column
                            caption={t('Kapasite')}
                            dataField="capacity"
                            alignment="center"
                            selectedFilterOperation="contains"


                            allowResizing={false}
                        />

                        <Column
                            caption={t('HAL OC Type')}
                            dataField="hal_type"
                            alignment="center"
                            selectedFilterOperation="contains"


                            allowResizing={true}
                            cellRender={({ data }) => {
                                if (!data.hal_type) return '-';
                                const halTypeMap = {
                                    1: 'Sevk Emri',
                                    2: 'Grupaj'
                                };
                                return halTypeMap[data.hal_type] || data.hal_type;
                            }}
                        />
                        <Column
                            caption={t('Origin CEDI')}
                            dataField="origin_cedi"
                            alignment="center"
                            selectedFilterOperation="contains"


                            allowResizing={true}
                        />
                        <Column
                            caption={t('İşlem')}
                            dataField="action"
                            alignment="center"

                            allowResizing={false}
                            allowExporting={false}
                            allowReordering={false}
                            allowSorting={false}
                            allowFiltering={false}
                            cellRender={({ data }) => <ActionButton data={data} onEdit={handleEdit} />}
                        />
                        <Column
                            caption={t('Geçerli')}
                            dataField="is_active"
                            alignment="center"
                            selectedFilterOperation="contains"


                            allowResizing={false}
                            cellRender={({ data }) => <StatusIcon isActive={data.is_active} />}
                        />
                        <Summary>
                            <ToolbarItem
                                name="toplam"
                                location="after"
                                text={t('Toplam')}
                            />
                        </Summary>
                    </DatagridCustom>
                </Row>
            </Card>

            <MemoizedDepoIslemleri
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                setTrigger={setTrigger}
                trigger={trigger}
                selected={selected}
            />

            <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>
                    {t('Depo Silme Formu')}
                </ModalHeader>
                <ModalBody>
                    {t('Depo Kodu')}: <b>{selected?.warehouse_code}</b> <br />
                    {t('Depo Silme İşlemi Yapmak İstediğinize Emin Misiniz?')}
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={handleDelete}>
                        {t('Sil')}
                    </Button>
                    <Button color="secondary" onClick={toggleDeleteModal}>
                        {t('İptal')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default React.memo(DepoTanim);